import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAcademy } from '../AcademyContext';
import Breadcrumb from '../components/Breadcrumb';
import ProgressBar from '../components/ProgressBar';
import VideoStep from '../components/VideoStep';
import ExerciseStep from '../components/ExerciseStep';
import CourseResult from '../components/CourseResult';

/**
 * Course path — a vertical, stacked sequence the user works through top to
 * bottom: video → exercise(s) → next video → … → final result.
 *
 * The stack is built by flattening lessons into steps so the backend can later
 * place any combination of videos and exercises in order.
 */
const CURRENT_STEP_ID = 'ac-current-step';

const CoursePage = () => {
  const { topicId, courseId } = useParams();
  const { getTopic, getCourse, getCourseProgress, getCourseSteps } = useAcademy();
  const topic = getTopic(topicId);
  const course = getCourse(topicId, courseId);

  // Ordered path steps (video → special → quiz) with unlock state.
  const steps = topic && course ? getCourseSteps(topicId, course) : [];
  const activeKey = steps.find((s) => s.active)?.key;

  // On load, bring the current step into focus instead of starting at the top.
  useEffect(() => {
    if (!activeKey) return;
    const el = document.getElementById(CURRENT_STEP_ID);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeKey]);

  if (!topic || !course) return <Navigate to="/academy" replace />;

  const { done, total, isComplete } = getCourseProgress(topicId, course);

  return (
    <main className="ac-page">
      <Breadcrumb
        items={[
          { label: 'All Topics', to: '/academy' },
          { label: topic.title, to: `/academy/${topic.id}` },
          { label: course.title },
        ]}
      />

      <div className="ac-course-head">
        <div className="ac-course-head-main">
          <p className="ac-eyebrow" style={{ color: topic.accent, margin: '0 0 8px' }}>
            {topic.title}
          </p>
          <h1 className="ac-course-name">{course.title}</h1>
        </div>
        <div className="ac-course-head-prog">
          <span className="ac-course-head-count">{done}/{total} done</span>
          <ProgressBar done={done} total={total} showLabel={false} />
        </div>
      </div>

      <div className="ac-steps">
        {steps.map((step, i) => {
          const stepNo = i + 1;
          const anchorId = step.active ? CURRENT_STEP_ID : undefined;
          if (step.type === 'video') {
            return (
              <VideoStep
                key={step.key}
                topicId={topicId}
                courseId={courseId}
                lesson={step.lesson}
                stepNo={stepNo}
                watched={step.done}
                locked={step.locked}
                active={step.active}
                isLast={false}
                anchorId={anchorId}
              />
            );
          }
          return (
            <ExerciseStep
              key={step.key}
              topicId={topicId}
              courseId={courseId}
              lesson={step.lesson}
              kind={step.type}
              stepNo={stepNo}
              done={step.done}
              locked={step.locked}
              active={step.active}
              isLast={false}
              anchorId={anchorId}
            />
          );
        })}

        {/* Final result is always the last step in the path. */}
        <CourseResult
          isComplete={isComplete}
          course={course}
          topicId={topicId}
          remaining={steps.filter((s) => !s.done).length}
        />
      </div>
    </main>
  );
};

export default CoursePage;
